import { IconButton, makeStyles, Paper, Typography } from '@material-ui/core'
import { useParams } from 'react-router-dom'
import { FixQuality, UserStat, useUserStat } from '../api'
import { FC } from 'react'
import { strings } from '../strings'
import { SyncRounded } from '@material-ui/icons'
import { formatDurationSince } from '../format-helpers'
import Card from 'common/Card'
import { clientMarkerColors } from 'Map/layers/ClientsLayer/constants'

export function UserStats() {
  const { userId } = useParams<any>()
  const { userStat, error, refresh } = useUserStat(userId)

  if (error) return <Paper>{error.toString()}</Paper>
  if (!userStat) return <Paper>Loading</Paper>

  return (
    <div>
      <CurrentStatus stat={userStat} onSync={() => refresh()} />
    </div>
  )
}

const CurrentStatus: FC<{ stat: UserStat; onSync: () => void }> = ({ stat, onSync }) => {
  const classes = useCurrentStatusStyles()

  /**
   * ex: $GPGGA,074811,3644.4762,N,05101.3239,E,4,35,1,0,M,-19.075000762939453,M,3,0*61
   * ___________________________________________________________________________________
   * - [0]  | Message ID                                         | $GPGGA
   * - [1]  | UTC Time                                           | 074811
   * - [2]  | Latitude                                           | 3644.4762
   * - [3]  | Latitude Hemisphere                                | N
   * - [4]  | Longitude                                          | 05101.3239
   * - [5]  | Longitude Hemisphere                               | E
   * - [6]  | Fix Quality                                        | 4
   * - [7]  | Number of Satellites                               | 35
   * - [8]  | Horizontal Dilution of Precision (HDOP)            | 1
   * - [9]  | Altitude                                           | 0
   * - [10] | Altitude Units (Meters)                            | M
   * - [11] | Geoidal Separation                                 | -19.075000762939453
   * - [12] | Geoidal Separation Units (Meters)                  | M
   * - [13] | Age of Differential GPS Data                       | 3
   * - [14] | Differential Reference Station ID and Checksum     | 0*61
   */
  const nmeaData = stat.lastNmea.split(',')

  const convertToDms = (value: string, dir: string) => {
    let degrees = 0
    let minutes = 0

    switch (dir) {
      /**latitude */
      case 'N':
      case 'S':
        degrees = parseInt(value.slice(0, 2), 10)
        minutes = parseFloat(value.slice(2))
        break

      /**longitude */
      case 'E':
      case 'W':
        degrees = parseInt(value.slice(0, 3), 10)
        minutes = parseFloat(value.slice(3))
        break

      default:
        return '_'
    }

    const seconds = (minutes % 1) * 60
    minutes = Math.floor(minutes)

    return `${degrees}\u00B0 ${minutes}' ${seconds.toFixed(2)}" ${dir}`
  }

  const getUtcTimeFromNmea = (utcTime: string) => {
    if (!utcTime) return '_'

    const hours = utcTime.slice(0, 2)
    const minutes = utcTime.slice(2, 4)
    const seconds = utcTime.slice(4, 6)
    return `${hours}:${minutes}:${seconds}`
  }

  return (
    <Card title={strings.userStatus} style={{ overflowY: 'auto' }}>
      <IconButton className={classes.sync} onClick={onSync}>
        <SyncRounded />
      </IconButton>
      <table className={classes.table}>
        <colgroup>
          <col className={classes.keyColumn} />
          <col className={classes.valueColumn} />
        </colgroup>
        <tbody>
          <tr>
            <td>
              <Typography>{strings.connectedTo}:</Typography>
            </td>
            <td>
              <Typography>{stat.mountpoint || strings.disconnected}</Typography>
            </td>
          </tr>
          <tr>
            <td>
              <Typography>{strings.connectedTime}:</Typography>
            </td>
            <td>
              <Typography>{formatDurationSince(stat.connectedSince)}</Typography>
            </td>
          </tr>
          <tr>
            <td>
              <Typography>{strings.rtcmCount}:</Typography>
            </td>
            <td>
              <Typography>{stat.rtcmCount}</Typography>
            </td>
          </tr>
          <tr>
            <td>
              <Typography>{strings.userAgent}:</Typography>
            </td>
            <td>
              <Typography>{stat.userAgent || strings.empty}</Typography>
            </td>
          </tr>
          <tr>
            <td>
              <Typography>{strings.IPAddr}:</Typography>
            </td>
            <td>
              <Typography>{stat.remote}</Typography>
            </td>
          </tr>
          {/* <tr>
            <td>Message ID</td>
            <td>{nmeaData[0]}</td>
          </tr> */}
          <tr>
            <td>UTC Time</td>
            <td>{getUtcTimeFromNmea(nmeaData[1])}</td>
          </tr>
          <tr>
            <td>Latitude</td>
            <td style={{ direction: 'ltr', textAlign: 'right' }}>
              {convertToDms(nmeaData[2], nmeaData[3])}
            </td>
          </tr>
          {/* <tr>
            <td>Latitude Hemisphere</td>
            <td>{nmeaData[3]}</td>
          </tr> */}
          <tr>
            <td>Longitude</td>
            <td style={{ direction: 'ltr', textAlign: 'right' }}>
              {convertToDms(nmeaData[4], nmeaData[5])}
            </td>
          </tr>
          {/* <tr>
            <td>Longitude Hemisphere</td>
            <td>{nmeaData[5]}</td>
          </tr> */}
          <tr>
            <td>Nav Mode</td>
            <td style={{ background: clientMarkerColors[parseInt(nmeaData[6]) as FixQuality] }}>
              {FixQuality[parseInt(nmeaData[6]) as FixQuality] ?? '_'}
            </td>
          </tr>
          <tr>
            <td>Sat Used</td>
            <td>{nmeaData[7]}</td>
          </tr>
          <tr>
            <td>HDOP</td>
            <td>{nmeaData[8]}</td>
          </tr>
          <tr>
            <td>Altitude</td>
            <td style={{ direction: 'ltr', textAlign: 'right' }}>
              {nmeaData[9] && nmeaData[10] ? `${nmeaData[9]} ${nmeaData[10].toLowerCase()}` : '_'}
            </td>
          </tr>
          <tr>
            <td>Geoidal Separation</td>
            <td style={{ direction: 'ltr', textAlign: 'right' }}>
              {nmeaData[11] && nmeaData[12] ? `${nmeaData[11]} ${nmeaData[12].toLowerCase()}` : '_'}
            </td>
          </tr>
          <tr>
            <td>Correction Age</td>
            <td>{nmeaData[13]}</td>
          </tr>
          <tr>
            <td>Reference ID</td>
            <td>{nmeaData[14]?.slice(0, nmeaData[14]?.indexOf('*'))}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  )
}

const useCurrentStatusStyles = makeStyles((theme) => ({
  table: {
    // padding: theme.spacing(1),
    overflow: 'auto',
    borderCollapse: 'separate',
    borderSpacing: theme.spacing(1, 1),
    '& td': {
      padding: theme.spacing(0.5, 1),
    },
  },
  keyColumn: {
    backgroundColor: '#e9e9e9',
    borderRadius: 4,
  },
  valueColumn: {},
  sync: {
    float: 'right',
  },
}))

// const NmeaQuery: FC<{ stat: UserStat }> = ({ stat }) => {
//   const classes = useNmeaQueryStyles()
//
//   if (stat.since === 0) return null
//
//   return <Paper className={classes.paper}></Paper>
// }
//
// const useNmeaQueryStyles = makeStyles((theme) => ({
//   paper: {
//     marginTop: theme.spacing(1),
//     paddingTop: theme.spacing(2),
//   },
// }))
